import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const empty = { full_name: "", email: "", mobile: "" };

export default function ModeratorsTab() {
  const [mods, setMods] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    const r = await api.get("/moderators");
    setMods(r.data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.full_name || !form.email || !form.mobile) return toast.error("Fill all moderator fields");
    setBusy(true);
    try {
      await api.post("/moderators", form);
      toast.success("Moderator added");
      setForm(empty);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add moderator"); }
    finally { setBusy(false); }
  };

  const del = async (m) => {
    try {
      await api.delete(`/moderators/${m.id}`);
      toast.success("Moderator removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Card className="p-6 border-slate-200 max-w-3xl" data-testid="moderator-tab">
      <div className="mb-5">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><UserPlus className="h-5 w-5 text-indigo-600" /> Add Moderator</h2>
        <p className="text-sm text-slate-500 mt-0.5">Add people (caretaker, manager) who help you manage the property.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <F label="Full name"><Input value={form.full_name} onChange={upd("full_name")} data-testid="moderator-name-input" /></F>
        <F label="Email"><Input type="email" value={form.email} onChange={upd("email")} data-testid="moderator-email-input" /></F>
        <F label="Mobile"><Input value={form.mobile} onChange={upd("mobile")} data-testid="moderator-mobile-input" /></F>
      </div>
      <div className="pt-4 flex justify-end">
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={add} disabled={busy} data-testid="moderator-add-button">
          {busy ? "Adding…" : "Add moderator"}
        </Button>
      </div>

      <div className="mt-6 border-t border-slate-100 pt-4">
        {mods.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">No moderators added yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {mods.map((m) => (
              <div key={m.id} className="py-3 flex items-center justify-between text-sm" data-testid={`moderator-row-${m.id}`}>
                <div>
                  <span className="font-medium">{m.full_name}</span>
                  <span className="text-slate-500 text-xs ml-3">{m.email} • {m.mobile}</span>
                </div>
                <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => del(m)} data-testid={`moderator-delete-${m.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
